import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { addBusinessDays, formatISO, parseISO } from 'date-fns';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useDayEventsBoard } from '../hooks/use-day-events-board.ts';
import { DaySkeleton } from './day-skeleton.tsx';
import { DayStrip } from './day-strip.tsx';
import { DaySuggestions } from './day-suggestions.tsx';
import { EmptyDay } from './empty-day.tsx';
import { NewEventButton } from './new-event-button.tsx';
import { OverlayEventCard } from './overlay-event-card.tsx';
import { SortableEventCard } from './sortable-event-card.tsx';

export type Props = {
    day: string;
    ownerId: string;
    onChangedDay: (day: string) => void;
};

type DayPaneProps = {
    day: string;
    ownerId: string;
};

// How many working days either side of the current one are ready to swipe to. Quick swipes in a row
// keep going until the scroll stops; only then does the day change and the row centre on it again.
const swipeReach = 10;
const paneOffsets = Array.from({ length: 2 * swipeReach + 1 }, (_, index) => index - swipeReach);
const currentPaneIndex = swipeReach;

// Without `scrollend` (older Safari), this long without a scroll event means the snap has settled
const scrollSettleMs = 120;
const supportsScrollEnd = 'onscrollend' in window;

/** One day's event cards, with the route suggestions below. */
function DayPane({ day, ownerId }: DayPaneProps): ReactNode {
    const today = formatISO(new Date(), { representation: 'date' });
    const { isLoading, events, sortedIds, draggedEvent, dndContextProps } = useDayEventsBoard({
        day,
        ownerId,
    });

    if (isLoading) {
        return <DaySkeleton />;
    }

    if (events.length === 0) {
        return <EmptyDay day={day} ownerId={ownerId} className="gap-relaxed pt-default" />;
    }

    return (
        <DndContext {...dndContextProps}>
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
                {events.map((event) => (
                    <SortableEventCard key={event.event_id} event={event} date={day} />
                ))}
            </SortableContext>
            <NewEventButton day={day} ownerId={ownerId} />
            {/* Suggestions are anchored on the day's visits; without any, there is no route to anchor on.
                Past days can no longer be planned. ISO date strings compare correctly as strings. */}
            {day >= today && (
                <div className="pt-default">
                    <DaySuggestions day={day} ownerId={ownerId} />
                </div>
            )}

            <DragOverlay>
                {draggedEvent ? <OverlayEventCard event={draggedEvent} date={day} /> : null}
            </DragOverlay>
        </DndContext>
    );
}

/**
 * One day as a centred column of event cards. Scrolling it sideways pages a day, as the day strip pages
 * a week, and skips the weekend as the header's arrows do.
 */
export function EventsOverviewBodyDay({ day, ownerId, onChangedDay }: Props): ReactNode {
    const sendEvent = useAnalytics();
    const verticalScrollRef = useRef<HTMLDivElement>(null);
    const daysRowRef = useRef<HTMLDivElement>(null);
    // The day in view while swiping, which the day strip follows before the day changes. It belongs to
    // the day the swipe started from: the router changes the day a moment after the swipe stops, so the
    // strip keeps the day in view until then, and shows the page's day once it has changed.
    const [swipe, setSwipe] = useState<{ fromDay: string; dayInView: string }>();
    const stripDay = swipe?.fromDay === day ? swipe.dayInView : day;

    const settleTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

    // The working days either side of the current one, which slide in as skeletons while swiping
    const date = parseISO(day);
    const paneDays = paneOffsets.map((offset) =>
        formatISO(addBusinessDays(date, offset), { representation: 'date' }),
    );

    // When the day changes, show it from the top, in the middle of the row
    useLayoutEffect(() => {
        verticalScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        const scroller = daysRowRef.current;
        if (scroller) {
            scroller.scrollLeft = currentPaneIndex * scroller.clientWidth;
        }
    }, [day]);

    // Rounded, as a snap can rest a fraction of a pixel off. The page's day while the width can't be read.
    const findDayInView = (scroller: HTMLDivElement): string =>
        paneDays[Math.round(scroller.scrollLeft / scroller.clientWidth)] ?? day;

    // Once the scroll has come to rest, the day in view becomes the day
    const pickRestingDay = (scroller: HTMLDivElement): void => {
        const restingDay = findDayInView(scroller);
        if (restingDay !== day) {
            sendEvent('event_calendar_period_changed', { direction: 'day_swipe', view_mode: 'day' });
            onChangedDay(restingDay);
        }
    };

    // While scrolling, the strip follows the day in view
    const followScroll = (scroller: HTMLDivElement): void => {
        const dayInView = findDayInView(scroller);
        if (swipe?.fromDay !== day || swipe.dayInView !== dayInView) {
            setSwipe({ fromDay: day, dayInView });
        }
        if (!supportsScrollEnd) {
            clearTimeout(settleTimeout.current);
            settleTimeout.current = setTimeout(() => {
                pickRestingDay(scroller);
            }, scrollSettleMs);
        }
    };

    const pickDay = (picked: string): void => {
        sendEvent('event_calendar_period_changed', { direction: 'day_strip', view_mode: 'day' });
        onChangedDay(picked);
    };

    return (
        <div ref={verticalScrollRef} className="h-full w-full overflow-y-auto">
            <div className="mx-auto flex min-h-full w-full max-w-lg flex-col gap-cozy">
                <DayStrip
                    day={stripDay}
                    onPick={pickDay}
                    className="sticky top-0 z-10 bg-container px-5 pt-default pb-cozy sm:px-4"
                />
                {/* The rest of the screen scrolls sideways, so an empty day swipes too. Each day is exactly
                    as wide as the scroller, with no gap, so the scroll position divided by the width is the
                    index of the day in view. */}
                <div
                    ref={daysRowRef}
                    onScroll={(event) => {
                        followScroll(event.currentTarget);
                    }}
                    // eslint-plugin-react doesn't know `onScrollEnd` yet; React 19 supports it
                    // eslint-disable-next-line react/no-unknown-property
                    onScrollEnd={(event) => {
                        pickRestingDay(event.currentTarget);
                    }}
                    className="flex grow snap-x snap-mandatory overflow-x-auto overscroll-x-contain scrollbar-none"
                >
                    {/* Only the current day loads; a day swiped past makes no request */}
                    {paneDays.map((paneDay, paneIndex) => (
                        <div
                            key={paneDay}
                            className="flex w-full shrink-0 snap-center snap-always flex-col gap-cozy px-5 pb-relaxed sm:px-4"
                        >
                            {paneIndex === currentPaneIndex ? (
                                <DayPane day={paneDay} ownerId={ownerId} />
                            ) : (
                                <DaySkeleton />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
